// ============================================================================
// src/lib/backupStatus.ts — Backup status types, contracts and pure evaluators
// ============================================================================
// F1d: Admin > Backups. Pure domain logic and formatting for operational backup
// visibility. Complies with Design Constitution and SECURITY_DR_RUNBOOK.md:
// - Healthy, Failed, Overdue, Not enabled and Unknown must reflect real, fresh evidence.
// - Dry-run dispatches ([dr-noexec]) must NEVER be counted as backups.
// - B2 and R2 provider results are reported separately; never inferred one from the other.
// - Schedule state, recovery test verification, and alert delivery are distinct.
// - Unknown defaults make NO assertions (never manufactured facts).
// - Strict validator enforces operational consistency and rejects invalid states.
// ============================================================================

export type BackupTier = 'frequent' | 'daily' | 'monthly';

export const BACKUP_TIERS: readonly BackupTier[] = Object.freeze(['frequent', 'daily', 'monthly']);

export type OverallCondition = 'healthy' | 'overdue' | 'failed' | 'not_enabled' | 'unknown';

export type ProviderCopyResult = 'success' | 'failed' | 'not_tested' | 'unknown';

export type ProviderStatus = {
  provider: 'Backblaze B2' | 'Cloudflare R2';
  bucket: string;
  objectLock: boolean | null;
  lastCopyResult: ProviderCopyResult;
  evidenceTime: string | null;
  details: string;
};

export type TierConclusion =
  | 'success'
  | 'failure'
  | 'cancelled'
  | 'timed_out'
  | 'in_progress'
  | 'no_recent_evidence'
  | 'never_run'
  | 'unknown';

export type TierHealthStatus = 'healthy' | 'warning' | 'critical' | 'no_recent_evidence' | 'never_run' | 'unknown';

export type TierStatus = {
  tier: BackupTier;
  lastAttemptAt: string | null;
  lastSuccessAt: string | null;
  ageMinutes: number | null;
  conclusion: TierConclusion;
  status: TierHealthStatus;
  coverage: 'database' | 'database_and_files' | 'unknown';
};

export type AdminBackupStatus = {
  condition: OverallCondition;
  conditionReason: string;
  checkedAt: string; // ISO observation timestamp
  latestAttempt: {
    id?: number | null;
    status: 'queued' | 'in_progress' | 'completed' | 'unknown';
    conclusion: 'success' | 'failure' | 'cancelled' | 'timed_out' | 'in_progress' | 'unknown';
    createdAt: string | null;
    startedAt?: string | null;
    completedAt: string | null;
    tier: BackupTier | 'unknown' | null;
    mode: 'execute' | 'non_execute' | 'unknown';
    runUrl: string | null;
  } | null;
  lastSuccess: {
    completedAt: string | null;
    tier: BackupTier | null;
    ageMinutes: number | null;
    coverage: 'database' | 'database_and_files' | 'unknown';
    runUrl: string | null;
  } | null;
  tiers: Record<BackupTier, TierStatus>;
  providers: {
    b2: ProviderStatus;
    r2: ProviderStatus;
  };
  schedule: {
    enabled: boolean | null;
    state: 'disabled' | 'enabled' | 'unknown';
    desiredState?: 'disabled' | 'enabled' | 'unknown';
    cadenceDescription: string;
    nextExpectedRun: string | null;
  };
  recovery: {
    lastVerifiedDrill: string | null;
    outcome: 'verified' | 'not_verified' | 'unknown';
    details: string;
  };
  alerts: {
    enabled: boolean | null;
    state: 'disabled' | 'enabled' | 'unknown';
    desiredState?: 'disabled' | 'enabled' | 'unknown';
    intendedRecipient: string | null;
    deliveryVerified: boolean | null;
    deliveryVerificationDetails: string;
  };
  searchWindow?: {
    totalObservedRuns: number;
    searchCoverageDescription: string;
  };
};

/** Format age in minutes into human readable text (tabular friendly). */
export function formatAgeMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes) || minutes < 0) {
    return '—';
  }
  if (minutes < 1) return '< 1 min';
  if (minutes < 60) return `${Math.floor(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMins = Math.floor(minutes % 60);
  if (hours < 24) {
    return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
}

/** Evaluate overall condition from component operational facts with strict evidence requirements. */
export function evaluateOverallCondition(input: {
  scheduleState?: 'disabled' | 'enabled' | 'unknown' | null;
  latestAttempt?: {
    status?: string | null;
    conclusion?: string | null;
    mode?: string | null;
  } | null;
  lastSuccess?: {
    ageMinutes?: number | null;
    completedAt?: string | null;
    coverage?: string | null;
  } | null;
  tierStatuses?: Partial<Record<BackupTier, TierStatus>> | null;
  providers?: {
    b2?: {lastCopyResult?: string | null} | null;
    r2?: {lastCopyResult?: string | null} | null;
  } | null;
  evidenceReachable?: boolean;
}): {condition: OverallCondition; reason: string} {
  if (input.evidenceReachable === false) {
    return {
      condition: 'unknown',
      reason: 'Backup status evidence source is unreachable or unconfigured.',
    };
  }

  // 1. A failed latest execute attempt takes immediate precedence over historical success
  if (
    input.latestAttempt &&
    input.latestAttempt.mode === 'execute' &&
    input.latestAttempt.conclusion &&
    input.latestAttempt.conclusion !== 'success' &&
    input.latestAttempt.conclusion !== 'in_progress'
  ) {
    return {
      condition: 'failed',
      reason: `Latest backup execution ended in ${input.latestAttempt.conclusion}.`,
    };
  }

  // 1b. A newer unknown-mode attempt must NOT leave older positive evidence appearing current
  if (input.latestAttempt && input.latestAttempt.mode === 'unknown') {
    return {
      condition: 'unknown',
      reason: 'Latest backup execution is in an unknown mode; unverified operation.',
    };
  }

  // 2. Storage provider failures check
  if (input.providers?.b2?.lastCopyResult === 'failed' || input.providers?.r2?.lastCopyResult === 'failed') {
    return {
      condition: 'failed',
      reason: 'One or more storage provider copy operations failed.',
    };
  }

  // 3. Schedule state: strictly requires enabled
  const schedState = input.scheduleState ?? 'unknown';
  if (schedState === 'disabled') {
    return {
      condition: 'not_enabled',
      reason: 'Automated backup schedule is not enabled.',
    };
  }
  if (schedState === 'unknown') {
    return {
      condition: 'unknown',
      reason: 'Automated backup schedule state is unverified.',
    };
  }

  // 4. Schedule is enabled: frequent tier check is mandatory
  const frequentTier = input.tierStatuses?.frequent;
  if (
    !frequentTier ||
    frequentTier.status === 'unknown' ||
    frequentTier.status === 'no_recent_evidence' ||
    frequentTier.status === 'never_run'
  ) {
    return {
      condition: 'unknown',
      reason: 'Frequent backup tier has no verified recent run within the observation window.',
    };
  }
  if (frequentTier.status === 'critical' || frequentTier.status === 'warning') {
    return {
      condition: 'overdue',
      reason: `Frequent backup tier is overdue (${formatAgeMinutes(frequentTier.ageMinutes)} old).`,
    };
  }

  // 4b. Tier newer failure check: a tier with a newer failed execution cannot declare health
  if (input.tierStatuses) {
    for (const [tierName, t] of Object.entries(input.tierStatuses)) {
      if (t && (t.status === 'critical' || t.conclusion === 'failure')) {
        return {
          condition: 'failed',
          reason: `${tierName} backup tier latest attempt ended in failure.`,
        };
      }
    }
  }

  // 5. Positive provider verification: BOTH B2 and R2 must have verified success with evidence times
  if (input.providers?.b2?.lastCopyResult !== 'success' || input.providers?.r2?.lastCopyResult !== 'success') {
    return {
      condition: 'unknown',
      reason: 'Provider storage copies are not positively verified.',
    };
  }

  // 6. Positive coverage verification: coverage must be explicitly database_and_files or database
  const cov = input.lastSuccess?.coverage;
  if (!cov || cov === 'unknown') {
    return {
      condition: 'unknown',
      reason: 'Backup content coverage is not positively verified.',
    };
  }

  // 7. Last success existence check and freshness
  if (
    !input.lastSuccess ||
    !input.lastSuccess.completedAt ||
    input.lastSuccess.ageMinutes === null ||
    input.lastSuccess.ageMinutes === undefined
  ) {
    return {
      condition: 'unknown',
      reason: 'No completed backup generation recorded.',
    };
  }

  return {
    condition: 'healthy',
    reason: 'Backups are running on schedule and storage copies are verified.',
  };
}

/** Default safe fallback state when operational evidence is missing/uninitialized. Assert NO unverified facts. */
export function defaultUnknownBackupStatus(checkedAt: string = new Date().toISOString()): AdminBackupStatus {
  return {
    condition: 'unknown',
    conditionReason: 'Operational backup evidence has not been loaded or verified.',
    checkedAt,
    latestAttempt: null,
    lastSuccess: null,
    tiers: {
      frequent: {
        tier: 'frequent',
        lastAttemptAt: null,
        lastSuccessAt: null,
        ageMinutes: null,
        conclusion: 'unknown',
        status: 'unknown',
        coverage: 'unknown',
      },
      daily: {
        tier: 'daily',
        lastAttemptAt: null,
        lastSuccessAt: null,
        ageMinutes: null,
        conclusion: 'unknown',
        status: 'unknown',
        coverage: 'unknown',
      },
      monthly: {
        tier: 'monthly',
        lastAttemptAt: null,
        lastSuccessAt: null,
        ageMinutes: null,
        conclusion: 'unknown',
        status: 'unknown',
        coverage: 'unknown',
      },
    },
    providers: {
      b2: {
        provider: 'Backblaze B2',
        bucket: 'wcf-planner-dr-primary-2026',
        objectLock: null,
        lastCopyResult: 'unknown',
        evidenceTime: null,
        details: 'Primary storage copy verification unknown.',
      },
      r2: {
        provider: 'Cloudflare R2',
        bucket: 'wcf-planner-dr-secondary-2026',
        objectLock: null,
        lastCopyResult: 'unknown',
        evidenceTime: null,
        details: 'Secondary storage copy verification unknown.',
      },
    },
    schedule: {
      enabled: null,
      state: 'unknown',
      desiredState: 'unknown',
      cadenceDescription:
        'Planned cadence: Frequent: every 30m (:17, :47) · Daily: 04:11 UTC · Monthly: 1st @ 04:23 UTC (effective schedule unverified)',
      nextExpectedRun: null,
    },
    recovery: {
      lastVerifiedDrill: null,
      outcome: 'unknown',
      details: 'No verified drill evidence found.',
    },
    alerts: {
      enabled: null,
      state: 'unknown',
      desiredState: 'unknown',
      intendedRecipient: null,
      deliveryVerified: null,
      deliveryVerificationDetails: 'Alert delivery status unverified.',
    },
    searchWindow: {
      totalObservedRuns: 0,
      searchCoverageDescription: 'No observations recorded.',
    },
  };
}

function isValidIsoTimestamp(str: unknown): str is string {
  if (typeof str !== 'string' || !str.trim()) return false;
  const ms = Date.parse(str);
  return Number.isFinite(ms);
}

function isSafeUrl(str: unknown): str is string {
  if (typeof str !== 'string') return false;
  try {
    const parsed = new URL(str);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

/** Deep validation of operational payload to prevent shallow-merging manufactured facts. */
export function validateBackupStatus(data: unknown): AdminBackupStatus {
  if (!data || typeof data !== 'object') {
    throw new Error('Payload must be a non-null object');
  }
  const obj = data as Record<string, unknown>;

  const validConditions = new Set(['healthy', 'overdue', 'failed', 'not_enabled', 'unknown']);
  if (typeof obj.condition !== 'string' || !validConditions.has(obj.condition)) {
    throw new Error(`Invalid or missing condition: ${String(obj.condition)}`);
  }
  if (typeof obj.conditionReason !== 'string' || !obj.conditionReason.trim()) {
    throw new Error('Missing or empty conditionReason');
  }
  if (!isValidIsoTimestamp(obj.checkedAt)) {
    throw new Error(`Invalid checkedAt timestamp: ${String(obj.checkedAt)}`);
  }

  // Tiers validation
  if (!obj.tiers || typeof obj.tiers !== 'object') {
    throw new Error('Missing tiers map');
  }
  const tiersObj = obj.tiers as Record<string, unknown>;
  const validTierConclusions = new Set([
    'success',
    'failure',
    'cancelled',
    'timed_out',
    'in_progress',
    'no_recent_evidence',
    'never_run',
    'unknown',
  ]);
  const validTierHealth = new Set(['healthy', 'warning', 'critical', 'no_recent_evidence', 'never_run', 'unknown']);
  const validCoverages = new Set(['database', 'database_and_files', 'unknown']);

  for (const t of ['frequent', 'daily', 'monthly'] as const) {
    const tier = tiersObj[t] as Record<string, unknown> | undefined;
    if (!tier || typeof tier !== 'object') {
      throw new Error(`Missing or empty tier: ${t}`);
    }
    if (tier.tier !== t) {
      throw new Error(`Tier key mismatch: expected ${t}, got ${String(tier.tier)}`);
    }
    if (typeof tier.conclusion !== 'string' || !validTierConclusions.has(tier.conclusion)) {
      throw new Error(`Invalid conclusion in tier ${t}: ${String(tier.conclusion)}`);
    }
    if (typeof tier.status !== 'string' || !validTierHealth.has(tier.status)) {
      throw new Error(`Invalid status in tier ${t}: ${String(tier.status)}`);
    }
    if (typeof tier.coverage !== 'string' || !validCoverages.has(tier.coverage)) {
      throw new Error(`Invalid coverage in tier ${t}: ${String(tier.coverage)}`);
    }
    if (tier.ageMinutes !== null && (!Number.isFinite(tier.ageMinutes) || (tier.ageMinutes as number) < 0)) {
      throw new Error(`Invalid ageMinutes in tier ${t}: ${String(tier.ageMinutes)}`);
    }
    if (tier.lastAttemptAt !== null && !isValidIsoTimestamp(tier.lastAttemptAt)) {
      throw new Error(`Invalid lastAttemptAt in tier ${t}: ${String(tier.lastAttemptAt)}`);
    }
    if (tier.lastSuccessAt !== null && !isValidIsoTimestamp(tier.lastSuccessAt)) {
      throw new Error(`Invalid lastSuccessAt in tier ${t}: ${String(tier.lastSuccessAt)}`);
    }
  }

  // Providers validation
  if (!obj.providers || typeof obj.providers !== 'object') {
    throw new Error('Missing providers map');
  }
  const providersObj = obj.providers as Record<string, unknown>;
  const validCopyResults = new Set(['success', 'failed', 'not_tested', 'unknown']);

  for (const p of ['b2', 'r2'] as const) {
    const provider = providersObj[p] as Record<string, unknown> | undefined;
    if (!provider || typeof provider !== 'object') {
      throw new Error(`Missing or empty provider data for ${p}`);
    }
    const expectedName = p === 'b2' ? 'Backblaze B2' : 'Cloudflare R2';
    if (provider.provider !== expectedName) {
      throw new Error(`Provider name mismatch for ${p}: expected ${expectedName}, got ${String(provider.provider)}`);
    }
    if (typeof provider.bucket !== 'string' || !provider.bucket.trim()) {
      throw new Error(`Missing or empty bucket for ${p}`);
    }
    if (typeof provider.lastCopyResult !== 'string' || !validCopyResults.has(provider.lastCopyResult)) {
      throw new Error(`Invalid lastCopyResult for ${p}: ${String(provider.lastCopyResult)}`);
    }
    if (provider.objectLock !== null && typeof provider.objectLock !== 'boolean') {
      throw new Error(`Invalid objectLock for ${p}: expected boolean or null, got ${String(provider.objectLock)}`);
    }
    if (provider.evidenceTime !== null && !isValidIsoTimestamp(provider.evidenceTime)) {
      throw new Error(`Invalid evidenceTime for ${p}: ${String(provider.evidenceTime)}`);
    }
    if (typeof provider.details !== 'string') {
      throw new Error(`Missing details string for ${p}`);
    }
  }

  // Schedule validation
  if (!obj.schedule || typeof obj.schedule !== 'object') {
    throw new Error('Missing schedule data');
  }
  const sched = obj.schedule as Record<string, unknown>;
  const validSchedStates = new Set(['disabled', 'enabled', 'unknown']);
  if (typeof sched.state !== 'string' || !validSchedStates.has(sched.state)) {
    throw new Error(`Invalid schedule state: ${String(sched.state)}`);
  }
  if (sched.enabled !== null && typeof sched.enabled !== 'boolean') {
    throw new Error(`Invalid schedule enabled flag: ${String(sched.enabled)}`);
  }
  if (typeof sched.cadenceDescription !== 'string') {
    throw new Error('Missing cadenceDescription in schedule');
  }

  // Recovery validation
  if (!obj.recovery || typeof obj.recovery !== 'object') {
    throw new Error('Missing recovery data');
  }
  const rec = obj.recovery as Record<string, unknown>;
  const validRecoveryOutcomes = new Set(['verified', 'not_verified', 'unknown']);
  if (typeof rec.outcome !== 'string' || !validRecoveryOutcomes.has(rec.outcome)) {
    throw new Error(`Invalid recovery outcome: ${String(rec.outcome)}`);
  }
  if (rec.lastVerifiedDrill !== null && !isValidIsoTimestamp(rec.lastVerifiedDrill)) {
    throw new Error(`Invalid lastVerifiedDrill timestamp: ${String(rec.lastVerifiedDrill)}`);
  }
  if (typeof rec.details !== 'string') {
    throw new Error('Missing recovery details');
  }

  // Alerts validation
  if (!obj.alerts || typeof obj.alerts !== 'object') {
    throw new Error('Missing alerts data');
  }
  const alerts = obj.alerts as Record<string, unknown>;
  const validAlertStates = new Set(['disabled', 'enabled', 'unknown']);
  if (typeof alerts.state !== 'string' || !validAlertStates.has(alerts.state)) {
    throw new Error(`Invalid alerts state: ${String(alerts.state)}`);
  }
  if (alerts.enabled !== null && typeof alerts.enabled !== 'boolean') {
    throw new Error(`Invalid alerts enabled flag: ${String(alerts.enabled)}`);
  }
  if (alerts.intendedRecipient !== null && typeof alerts.intendedRecipient !== 'string') {
    throw new Error('intendedRecipient must be string or null');
  }
  if (alerts.deliveryVerified !== null && typeof alerts.deliveryVerified !== 'boolean') {
    throw new Error('deliveryVerified must be boolean or null');
  }

  // Deep latestAttempt validation
  if (obj.latestAttempt !== null && obj.latestAttempt !== undefined) {
    if (typeof obj.latestAttempt !== 'object') {
      throw new Error('latestAttempt must be an object or null');
    }
    const attempt = obj.latestAttempt as Record<string, unknown>;

    // Valid run identity: id must be a positive finite integer or null
    if (attempt.id !== null && attempt.id !== undefined) {
      if (
        typeof attempt.id !== 'number' ||
        !Number.isFinite(attempt.id) ||
        attempt.id <= 0 ||
        !Number.isInteger(attempt.id)
      ) {
        throw new Error(`Invalid attempt id: ${String(attempt.id)}`);
      }
    }

    // Status enum
    const validAttemptStatuses = new Set(['queued', 'in_progress', 'completed', 'unknown']);
    if (typeof attempt.status !== 'string' || !validAttemptStatuses.has(attempt.status)) {
      throw new Error(`Invalid latestAttempt status: ${String(attempt.status)}`);
    }

    // Conclusion enum (must be string, never object)
    const validAttemptConclusions = new Set(['success', 'failure', 'cancelled', 'timed_out', 'in_progress', 'unknown']);
    if (typeof attempt.conclusion !== 'string' || !validAttemptConclusions.has(attempt.conclusion)) {
      throw new Error(`Invalid latestAttempt conclusion: ${String(attempt.conclusion)}`);
    }

    // Mode enum
    const validModes = new Set(['execute', 'non_execute', 'unknown']);
    if (typeof attempt.mode !== 'string' || !validModes.has(attempt.mode)) {
      throw new Error(`Invalid latestAttempt mode: ${String(attempt.mode)}`);
    }

    // Tier enum
    const validTiers = new Set(['frequent', 'daily', 'monthly', 'unknown']);
    if (attempt.tier !== null && (typeof attempt.tier !== 'string' || !validTiers.has(attempt.tier))) {
      throw new Error(`Invalid latestAttempt tier: ${String(attempt.tier)}`);
    }

    // Timestamps: reject nonsense timestamps
    if (attempt.createdAt !== null && attempt.createdAt !== undefined) {
      if (!isValidIsoTimestamp(attempt.createdAt)) {
        throw new Error(`Invalid createdAt timestamp in latestAttempt: ${String(attempt.createdAt)}`);
      }
    }
    if (attempt.startedAt !== null && attempt.startedAt !== undefined) {
      if (!isValidIsoTimestamp(attempt.startedAt)) {
        throw new Error(`Invalid startedAt timestamp in latestAttempt: ${String(attempt.startedAt)}`);
      }
    }
    if (attempt.completedAt !== null && attempt.completedAt !== undefined) {
      if (!isValidIsoTimestamp(attempt.completedAt)) {
        throw new Error(`Invalid completedAt timestamp in latestAttempt: ${String(attempt.completedAt)}`);
      }
      if (attempt.status !== 'completed') {
        throw new Error(`completedAt must be null when status is ${attempt.status}`);
      }
    } else if (attempt.status === 'completed' && attempt.conclusion === 'success') {
      throw new Error('completedAt must not be null when status is completed and conclusion is success');
    }

    // runUrl safe scheme
    if (attempt.runUrl !== null && attempt.runUrl !== undefined && !isSafeUrl(attempt.runUrl)) {
      throw new Error(`Unsafe or invalid runUrl in latestAttempt: ${String(attempt.runUrl)}`);
    }
  }

  // Deep lastSuccess validation
  if (obj.lastSuccess !== null && obj.lastSuccess !== undefined) {
    if (typeof obj.lastSuccess !== 'object') {
      throw new Error('lastSuccess must be an object or null');
    }
    const success = obj.lastSuccess as Record<string, unknown>;

    const validTiers = new Set(['frequent', 'daily', 'monthly']);
    if (success.tier !== null && (typeof success.tier !== 'string' || !validTiers.has(success.tier))) {
      throw new Error(`Invalid lastSuccess tier: ${String(success.tier)}`);
    }

    const validCoverages = new Set(['database', 'database_and_files', 'unknown']);
    if (typeof success.coverage !== 'string' || !validCoverages.has(success.coverage)) {
      throw new Error(`Invalid lastSuccess coverage: ${String(success.coverage)}`);
    }

    if (success.ageMinutes !== null && success.ageMinutes !== undefined) {
      if (typeof success.ageMinutes !== 'number' || !Number.isFinite(success.ageMinutes) || success.ageMinutes < 0) {
        throw new Error(`Invalid lastSuccess ageMinutes: ${String(success.ageMinutes)}`);
      }
    }

    if (success.completedAt !== null && success.completedAt !== undefined) {
      if (!isValidIsoTimestamp(success.completedAt)) {
        throw new Error(`Invalid completedAt timestamp in lastSuccess: ${String(success.completedAt)}`);
      }
    }

    if (success.runUrl !== null && success.runUrl !== undefined && !isSafeUrl(success.runUrl)) {
      throw new Error(`Unsafe or invalid runUrl in lastSuccess: ${String(success.runUrl)}`);
    }
  }

  // Consistency check: 'healthy' overall condition MUST have positive verification
  if (obj.condition === 'healthy') {
    if (sched.state !== 'enabled' || sched.enabled !== true) {
      throw new Error('Inconsistent payload: condition is healthy but schedule is not enabled');
    }
    const frequent = tiersObj.frequent as Record<string, unknown>;
    if (frequent.status !== 'healthy') {
      throw new Error('Inconsistent payload: condition is healthy but frequent tier is not healthy');
    }
    if (frequent.conclusion !== 'success') {
      throw new Error('Inconsistent payload: condition is healthy but frequent conclusion is not success');
    }
    if (frequent.lastSuccessAt === null || !isValidIsoTimestamp(frequent.lastSuccessAt)) {
      throw new Error('Inconsistent payload: condition is healthy but frequent tier lastSuccessAt is null or invalid');
    }
    if (typeof frequent.ageMinutes !== 'number' || frequent.ageMinutes < 0 || frequent.ageMinutes > 120) {
      throw new Error(
        'Inconsistent payload: condition is healthy but frequent tier ageMinutes is null, negative, or overdue (>120)',
      );
    }

    const b2 = providersObj.b2 as Record<string, unknown>;
    const r2 = providersObj.r2 as Record<string, unknown>;
    if (b2.lastCopyResult !== 'success' || r2.lastCopyResult !== 'success') {
      throw new Error('Inconsistent payload: condition is healthy but provider copies are not verified success');
    }
    if (b2.evidenceTime === null || !isValidIsoTimestamp(b2.evidenceTime)) {
      throw new Error('Inconsistent payload: condition is healthy but B2 provider evidenceTime is null or invalid');
    }
    if (r2.evidenceTime === null || !isValidIsoTimestamp(r2.evidenceTime)) {
      throw new Error('Inconsistent payload: condition is healthy but R2 provider evidenceTime is null or invalid');
    }

    if (!obj.lastSuccess || typeof obj.lastSuccess !== 'object') {
      throw new Error('Inconsistent payload: condition is healthy but lastSuccess is missing');
    }
    const ls = obj.lastSuccess as Record<string, unknown>;
    if (ls.completedAt === null || !isValidIsoTimestamp(ls.completedAt)) {
      throw new Error('Inconsistent payload: condition is healthy but lastSuccess completedAt is null or invalid');
    }
    if (typeof ls.ageMinutes !== 'number' || ls.ageMinutes < 0 || ls.ageMinutes > 120) {
      throw new Error(
        'Inconsistent payload: condition is healthy but lastSuccess ageMinutes is null, negative, or overdue (>120)',
      );
    }
    if (ls.coverage !== 'database_and_files' && ls.coverage !== 'database') {
      throw new Error('Inconsistent payload: condition is healthy but coverage is unknown');
    }

    if (obj.latestAttempt) {
      const at = obj.latestAttempt as Record<string, unknown>;
      if (typeof at.id !== 'number' || !Number.isFinite(at.id) || at.id <= 0 || !Number.isInteger(at.id)) {
        throw new Error('Inconsistent payload: condition is healthy but latestAttempt has null or invalid id');
      }
      if (at.status !== 'completed') {
        throw new Error(`Inconsistent payload: condition is healthy but latestAttempt status is ${String(at.status)}`);
      }
      if (at.conclusion !== 'success') {
        throw new Error(
          `Inconsistent payload: condition is healthy but latestAttempt conclusion is ${String(at.conclusion)}`,
        );
      }
      if (at.mode !== 'execute') {
        throw new Error(`Inconsistent payload: condition is healthy but latestAttempt mode is ${String(at.mode)}`);
      }
      if (typeof at.createdAt !== 'string' || !isValidIsoTimestamp(at.createdAt)) {
        throw new Error('Inconsistent payload: condition is healthy but latestAttempt createdAt is null or invalid');
      }
      if (typeof at.completedAt !== 'string' || !isValidIsoTimestamp(at.completedAt)) {
        throw new Error('Inconsistent payload: condition is healthy but latestAttempt completedAt is null or invalid');
      }
    }
  }

  return obj as unknown as AdminBackupStatus;
}
